/* eslint-disable react/jsx-no-useless-fragment */
import { HTMLAttributes, useEffect, useState } from 'react';

import { CaretDownIcon, HeadingIcon, TextIcon, TextNoneIcon } from '@radix-ui/react-icons';
import { useEditorState, type Editor } from '@tiptap/react';

import { Button } from '../../../../primitives/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../../primitives/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../primitives/popover';

import { ListNotionItem } from '../../../list/list-notion-item';

import { cn } from '../../../../utils/cn';
import { Values } from '../../../../utils/types';
import { type List } from './list.type';

// The `CommandInput` uses this value to filter
const COMMAND_ID = {
  text: 'Texto',
  h1: 'Encabezado 1 h1',
  h2: 'Encabezado 2 h2',
  h3: 'Encabezado 3 h3',
  h4: 'Encabezado 4 h4',
  h5: 'Encabezado 5 h5',
  h6: 'Encabezado 6 h6',
} as const;

const COMMANDS: List<typeof COMMAND_ID>[] = [
  {
    id: COMMAND_ID.text,
    title: 'Texto',
    description: 'Simple y sencillo.',
    icon: <TextIcon />,
    shortcut: (
      <>
        <span>⌘⌥</span>
        <span className="inline-block w-3 text-right">0</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().setParagraph().run(),
  },
  {
    id: COMMAND_ID.h1,
    title: 'Encabezado 1',
    description: 'Título principal.',
    icon: (
      <div className="flex">
        <HeadingIcon />
        <span className="text-xs">1</span>
      </div>
    ),
    shortcut: (
      <>
        <span>⌘⌥</span>
        <span className="inline-block w-3 text-right">1</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    id: COMMAND_ID.h2,
    title: 'Encabezado 2',
    description: 'Título secundario.',
    icon: (
      <div className="flex">
        <HeadingIcon />
        <span className="text-xs">2</span>
      </div>
    ),
    shortcut: (
      <>
        <span>⌘⌥</span>
        <span className="inline-block w-3 text-right">2</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    id: COMMAND_ID.h3,
    title: 'Encabezado 3',
    description: 'Título de sección.',
    icon: (
      <div className="flex">
        <HeadingIcon />
        <span className="text-xs">3</span>
      </div>
    ),
    shortcut: (
      <>
        <span>⌘⌥</span>
        <span className="inline-block w-3 text-right">3</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    id: COMMAND_ID.h4,
    title: 'Encabezado 4',
    description: 'Título de sección.',
    icon: (
      <div className="flex">
        <HeadingIcon />
        <span className="text-xs">4</span>
      </div>
    ),
    shortcut: (
      <>
        <span>⌘⌥</span>
        <span className="inline-block w-3 text-right">4</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleHeading({ level: 4 }).run(),
  },
  {
    id: COMMAND_ID.h5,
    title: 'Encabezado 5',
    description: 'Título de sección.',
    icon: (
      <div className="flex">
        <HeadingIcon />
        <span className="text-xs">5</span>
      </div>
    ),
    shortcut: (
      <>
        <span>⌘⌥</span>
        <span className="inline-block w-3 text-right">5</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleHeading({ level: 5 }).run(),
  },
  {
    id: COMMAND_ID.h6,
    title: 'Encabezado 6',
    description: 'Título de sección.',
    icon: (
      <div className="flex">
        <HeadingIcon />
        <span className="text-xs">6</span>
      </div>
    ),
    shortcut: (
      <>
        <span>⌘⌥</span>
        <span className="inline-block w-3 text-right">6</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleHeading({ level: 6 }).run(),
  },
];

interface ComboboxTextProps extends Pick<HTMLAttributes<HTMLDivElement>, 'className'> {
  editor: Editor;
}

export function ComboboxText({ className, editor }: ComboboxTextProps) {
  const [open, setOpen] = useState(false);
  const [option, setOption] = useState<Values<typeof COMMAND_ID> | ''>(''); // only one of these commands can be executed at a time.

  const editorState = useEditorState({
    editor,
    selector: ({ editor }: { editor: Editor }) => ({
      isParagraph: editor.isActive('paragraph'),
      isHeading1: editor.isActive('heading', { level: 1 }),
      isHeading2: editor.isActive('heading', { level: 2 }),
      isHeading3: editor.isActive('heading', { level: 3 }),
      isHeading4: editor.isActive('heading', { level: 4 }),
      isHeading5: editor.isActive('heading', { level: 5 }),
      isHeading6: editor.isActive('heading', { level: 6 }),
    }),
  });

  useEffect(() => {
    if (editorState.isParagraph) {
      setOption(COMMAND_ID.text);
      return;
    }
    if (editorState.isHeading1) {
      setOption(COMMAND_ID.h1);
      return;
    }
    if (editorState.isHeading2) {
      setOption(COMMAND_ID.h2);
      return;
    }
    if (editorState.isHeading3) {
      setOption(COMMAND_ID.h3);
      return;
    }
    if (editorState.isHeading4) {
      setOption(COMMAND_ID.h4);
      return;
    }
    if (editorState.isHeading5) {
      setOption(COMMAND_ID.h5);
      return;
    }
    if (editorState.isHeading6) {
      setOption(COMMAND_ID.h6);
      return;
    }
    setOption('');
  }, [editorState]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={!editor.isEditable}>
        <Button
          aria-label="Opciones de formato de texto"
          title="Opciones de formato de texto"
          className={cn(
            'w-14 justify-between gap-0 rounded-full px-2',
            {
              'bg-accent/50': option !== '',
            },
            className,
          )}
          role="combobox"
          variant="ghost"
        >
          {option === '' ? (
            <TextNoneIcon className="size-4" />
          ) : option === COMMAND_ID.text ? (
            <TextIcon className="size-4" />
          ) : (
            <div className="flex">
              <HeadingIcon className="size-4" />
              <span className="text-xs">{getHeaderLevel(option)}</span>
            </div>
          )}
          <CaretDownIcon className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0">
        <Command defaultValue={option}>
          <CommandInput placeholder="Buscar" />
          <CommandList className="max-h-[12.5rem]">
            <CommandEmpty>Sin resultados 🫥</CommandEmpty>
            <CommandGroup>
              {COMMANDS.map(({ id, title, description, icon, shortcut, command }) => (
                <CommandItem
                  className={cn('mb-1 last:mb-0 [&:hover_*]:text-accent-foreground!', {
                    'bg-accent/50 text-accent-foreground': option === id,
                  })}
                  data-active={option === id ? 'true' : undefined}
                  key={id}
                  value={id}
                  onSelect={() => {
                    command(editor);
                    setOpen(false);
                  }}
                >
                  <ListNotionItem
                    title={title}
                    description={description}
                    icon={icon}
                    shortcut={shortcut}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function getHeaderLevel(id: Values<typeof COMMAND_ID>) {
  if (id === COMMAND_ID.h1) return '1';
  if (id === COMMAND_ID.h2) return '2';
  if (id === COMMAND_ID.h3) return '3';
  if (id === COMMAND_ID.h4) return '4';
  if (id === COMMAND_ID.h5) return '5';
  if (id === COMMAND_ID.h6) return '6';
  return '';
}
